import { Component, OnDestroy, OnInit } from '@angular/core'
import { collection, doc, getDoc, getDocs, onSnapshot, orderBy, query, where, Unsubscribe } from '@angular/fire/firestore'
import { FormControl, FormGroup } from '@angular/forms'
import { AdminClient, CrmAssignee, FirestoreStructure, LeadStatus, Merchant } from '@parkupp/core'
import { ApexAxisChartSeries } from 'ng-apexcharts'
import { AdminService } from 'src/app/services/admin.service'
import { MerchantService } from 'src/app/services/merchant.service'
import { GlobalVariablesService } from 'src/app/services/global-variables.service'
import { firstValueFrom } from 'rxjs'

interface BlockChart {
    series: ApexAxisChartSeries
    categories: string[]
    colors?: string[]
}

interface CampaignRow {
    campaign: string
    leads: number
    conversions: number
    subscriptions: number
    conversionRate: number
    totalBays: number
    actualValue: number
    netRevenue: number
}

interface SourceRow {
    source: string
    leads: number
    conversions: number
    subscriptions: number
    conversionRate: number
    totalBays: number
    actualValue: number
    netRevenue: number
    isGoogleAds: boolean
}

interface MonthBucket {
    label: string
    leads: number
    googleAdsLeads: number
    conversions: number
}

const GOOGLE_ADS_SOURCES = ['google', 'google ads', 'google_ads', 'googleads', 'google adwords', 'adwords']

const CHART_PALETTE = ['#3e8bff', '#4bd181', '#f59e0b', '#ef4444', '#a78bfa', '#14b8a6', '#f97316', '#6366f1', '#ec4899', '#84cc16', '#06b6d4', '#d946ef', '#0ea5e9', '#eab308', '#64748b']

function stableColorForString(str: string, palette: string[]): string {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash)
        hash = hash & hash
    }
    return palette[Math.abs(hash) % palette.length]
}

@Component({
    selector: 'app-admin-marketing-health',
    templateUrl: './admin-marketing-health.component.html',
    styleUrls: ['./admin-marketing-health.component.scss'],
})
export class AdminMarketingHealthComponent implements OnInit, OnDestroy {
    isLoading = true
    allClients: AdminClient[] = []
    googleAdsClients: AdminClient[] = []

    filterForm: FormGroup = new FormGroup({
        filterDateRange: new FormControl('last3months'),
        customStartDate: new FormControl(''),
        customEndDate: new FormControl(''),
    })

    dateRangeOptions = [
        { value: 'thismonth', label: 'This month' },
        { value: 'lastmonth', label: 'Last month' },
        { value: 'last7days', label: 'Last 7 days' },
        { value: 'last30days', label: 'Last 30 days' },
        { value: 'last3months', label: 'Last 3 months' },
        { value: 'last6months', label: 'Last 6 months' },
        { value: 'thisyear', label: 'This year' },
        { value: 'last12months', label: 'Last 12 months' },
        { value: 'alltime', label: 'All time' },
        { value: 'custom', label: 'Custom range' },
    ]

    adSpend: number | null = null

    sourceRows: SourceRow[] = []
    campaignRows: CampaignRow[] = []
    campaignSortColumn: keyof CampaignRow = 'leads'
    campaignSortDirection: 'asc' | 'desc' = 'desc'

    sourceChart: BlockChart = { series: [], categories: [] }
    funnelChart: BlockChart = { series: [], categories: [] }
    assigneeChart: BlockChart = { series: [], categories: [] }
    trendChart: BlockChart = { series: [], categories: [] }

    crmAssigneeMap: { [key: string]: CrmAssignee } = {}

    readonly barChartConfig = {
        type: 'bar' as const,
        height: 280,
        toolbar: { show: false },
    }
    readonly trendChartConfig = {
        type: 'bar' as const,
        height: 320,
        toolbar: { show: false },
        stacked: false,
    }
    readonly plotOptions = {
        bar: { horizontal: true, barHeight: '60%', distributed: true, borderRadius: 4 },
    }
    readonly dataLabels = {
        enabled: true,
        style: { fontSize: '12px', fontWeight: 600 },
    }

    private dataUnsubscribe: Unsubscribe | null = null
    private merchantMap = new Map<string, Merchant>()
    private subCommissionMap = new Map<string, number>()

    constructor(
        private adminSvc: AdminService,
        private merchantService: MerchantService,
        public globalVariablesService: GlobalVariablesService
    ) {}

    async ngOnInit(): Promise<void> {
        await Promise.all([this.loadMerchants(), this.loadCrmAssignees()])
        this.loadData()
        this.filterForm.valueChanges.subscribe(() => {
            this.loadData()
        })
    }

    private async loadCrmAssignees(): Promise<void> {
        const snapshot = await getDocs(query(collection(this.adminSvc.firestore, FirestoreStructure.CRM_ASSIGNEES), where('active', '==', true)))
        this.crmAssigneeMap = {}
        snapshot.docs.forEach((d) => {
            const assignee = new CrmAssignee(d.data(), d.id)
            if (assignee.$key) {
                this.crmAssigneeMap[assignee.$key] = assignee
            }
        })
    }

    private async loadMerchants(): Promise<void> {
        const merchants = await firstValueFrom(this.merchantService.listAll())
        this.merchantMap.clear()
        for (const merchant of merchants) {
            if (merchant.$key) {
                this.merchantMap.set(merchant.$key, merchant)
            }
        }
    }

    private async loadSubscriptionCommissions(): Promise<void> {
        this.subCommissionMap.clear()
        const converted = this.googleAdsClients.filter((c) => c.leadStatus === LeadStatus.CONVERTED)
        const subKeys: string[] = []
        for (const client of converted) {
            const summaries = (client as any).subscriptionSummaries
            if (summaries) {
                subKeys.push(...Object.keys(summaries))
            }
        }

        const fetches = subKeys.map(async (subKey) => {
            try {
                const subDoc = await getDoc(doc(this.adminSvc.firestore, FirestoreStructure.SUBSCRIPTIONS, subKey))
                if (subDoc.exists()) {
                    const merchantKey = subDoc.data()['merchantRef']?.id
                    if (merchantKey) {
                        const merchant = this.merchantMap.get(merchantKey)
                        const commPct = Number(merchant?.commissionPercentage ?? 15)
                        this.subCommissionMap.set(subKey, commPct)
                    }
                }
            } catch {
                // Subscription doc may not exist in emulator; fall back to default
            }
        })
        await Promise.all(fetches)
    }

    ngOnDestroy(): void {
        if (this.dataUnsubscribe) {
            this.dataUnsubscribe()
            this.dataUnsubscribe = null
        }
    }

    get isCustomDateRange(): boolean {
        return this.filterForm.get('filterDateRange')?.value === 'custom'
    }

    get dateRangeLabel(): string {
        const filterDateRange = this.filterForm.get('filterDateRange')?.value
        const option = this.dateRangeOptions.find((o) => o.value === filterDateRange)
        return option?.label || ''
    }

    isGoogleAdsSource(source: string | undefined): boolean {
        if (!source) return false
        return GOOGLE_ADS_SOURCES.includes(source.toLowerCase().trim())
    }

    // --- KPI Getters ---

    get totalLeads(): number {
        return this.allClients.length
    }

    get googleAdsLeadCount(): number {
        return this.googleAdsClients.length
    }

    get googleAdsPercentage(): number {
        if (this.totalLeads === 0) return 0
        return (this.googleAdsLeadCount / this.totalLeads) * 100
    }

    get googleAdsConversions(): number {
        return this.googleAdsClients.filter((c) => c.leadStatus === LeadStatus.CONVERTED).length
    }

    get googleAdsTotalSubscriptions(): number {
        return this.googleAdsClients
            .filter((c) => c.leadStatus === LeadStatus.CONVERTED)
            .reduce((sum, c) => {
                const summaries = (c as any).subscriptionSummaries
                if (!summaries) return sum
                return sum + Object.values(summaries).filter((sub: any) => sub.status === 'Active' || sub.status === 'Completed').length
            }, 0)
    }

    get googleAdsConversionRate(): number {
        if (this.googleAdsLeadCount === 0) return 0
        return (this.googleAdsConversions / this.googleAdsLeadCount) * 100
    }

    get actualSubscriptionValue(): number {
        return this.googleAdsClients
            .filter((c) => c.leadStatus === LeadStatus.CONVERTED)
            .reduce((sum, c) => {
                const summaries = (c as any).subscriptionSummaries
                if (!summaries) return sum
                return (
                    sum +
                    Object.values(summaries).reduce((subSum: number, sub: any) => {
                        if (sub.status === 'Active' || sub.status === 'Completed') {
                            return subSum + ((sub.feeInCents || 0) / 100) * (sub.baysBooked || 1) * (sub.period || 1)
                        }
                        return subSum
                    }, 0)
                )
            }, 0)
    }

    get netRevenue(): number {
        const vars = this.globalVariablesService.currentVariables
        const gatewayRate = (vars?.paygateGatewayFeePercentage ?? 3.5) / 100

        return this.googleAdsClients
            .filter((c) => c.leadStatus === LeadStatus.CONVERTED)
            .reduce((sum, c) => {
                const summaries = (c as any).subscriptionSummaries
                if (!summaries) return sum
                return (
                    sum +
                    Object.entries(summaries).reduce((subSum: number, [subKey, sub]: [string, any]) => {
                        if (sub.status === 'Active' || sub.status === 'Completed') {
                            const subValue = ((sub.feeInCents || 0) / 100) * (sub.baysBooked || 1) * (sub.period || 1)
                            const commPct = this.subCommissionMap.get(subKey) ?? 15
                            const serviceFee = subValue * (commPct / 100)
                            const gatewayFee = subValue * gatewayRate
                            return subSum + serviceFee - gatewayFee
                        }
                        return subSum
                    }, 0)
                )
            }, 0)
    }

    get costPerLead(): number | null {
        if (!this.adSpend || this.googleAdsLeadCount === 0) return null
        return this.adSpend / this.googleAdsLeadCount
    }

    get costPerAcquisition(): number | null {
        if (!this.adSpend || this.googleAdsConversions === 0) return null
        return this.adSpend / this.googleAdsConversions
    }

    get returnOnAdSpend(): number | null {
        if (!this.adSpend || this.adSpend === 0) return null
        if (this.netRevenue === 0) return null
        return this.netRevenue / this.adSpend
    }

    // --- Data Loading ---

    private loadData(): void {
        if (this.dataUnsubscribe) {
            this.dataUnsubscribe()
            this.dataUnsubscribe = null
        }

        this.isLoading = true

        let statsRef = query(collection(this.adminSvc.firestore, FirestoreStructure.ADMIN_CLIENTS))

        const filterDateRange = this.filterForm.get('filterDateRange')?.value
        const dateRangeStart = this.getDateRangeStart(filterDateRange)
        const dateRangeEnd = this.getDateRangeEnd(filterDateRange)

        if (dateRangeStart) {
            if (dateRangeEnd) {
                statsRef = query(statsRef, where('createdAt', '>=', dateRangeStart), where('createdAt', '<=', dateRangeEnd), orderBy('createdAt', 'desc'))
            } else {
                statsRef = query(statsRef, where('createdAt', '>=', dateRangeStart), orderBy('createdAt', 'desc'))
            }
        } else {
            statsRef = query(statsRef, orderBy('createdAt', 'desc'))
        }

        this.dataUnsubscribe = onSnapshot(statsRef, async (snapshot) => {
            this.allClients = snapshot.docs.map((d) => new AdminClient({ ...d.data(), $key: d.id }))
            this.googleAdsClients = this.allClients.filter((c) => this.isGoogleAdsSource(c.p_source))

            await this.loadSubscriptionCommissions()

            this.buildSourceRows()
            this.buildCampaignRows()
            this.buildSourceChart()
            this.buildFunnelChart()
            this.buildAssigneeChart()
            this.buildTrendChart()
            this.isLoading = false
        })
    }

    // --- Source Comparison ---

    private buildSourceRows(): void {
        const vars = this.globalVariablesService.currentVariables
        const gatewayRate = (vars?.paygateGatewayFeePercentage ?? 3.5) / 100

        const sourceMap = new Map<string, { leads: number; conversions: number; subscriptions: number; totalBays: number; actualValue: number; netRevenue: number }>()

        for (const client of this.allClients) {
            const source = client.p_source || 'Unknown'
            const entry = sourceMap.get(source) || { leads: 0, conversions: 0, subscriptions: 0, totalBays: 0, actualValue: 0, netRevenue: 0 }
            entry.leads++
            entry.totalBays += client.numberOfBays || 0

            if (client.leadStatus === LeadStatus.CONVERTED) {
                entry.conversions++
                const summaries = (client as any).subscriptionSummaries
                if (summaries) {
                    for (const [subKey, sub] of Object.entries(summaries) as [string, any][]) {
                        if (sub.status === 'Active' || sub.status === 'Completed') {
                            entry.subscriptions++
                            const subValue = ((sub.feeInCents || 0) / 100) * (sub.baysBooked || 1) * (sub.period || 1)
                            const commPct = this.subCommissionMap.get(subKey) ?? 15
                            entry.actualValue += subValue
                            entry.netRevenue += subValue * (commPct / 100) - subValue * gatewayRate
                        }
                    }
                }
            }
            sourceMap.set(source, entry)
        }

        this.sourceRows = Array.from(sourceMap.entries())
            .map(([source, data]) => ({
                source,
                leads: data.leads,
                conversions: data.conversions,
                subscriptions: data.subscriptions,
                conversionRate: data.leads > 0 ? (data.conversions / data.leads) * 100 : 0,
                totalBays: data.totalBays,
                actualValue: data.actualValue,
                netRevenue: data.netRevenue,
                isGoogleAds: this.isGoogleAdsSource(source),
            }))
            .sort((a, b) => b.leads - a.leads)
    }

    private buildSourceChart(): void {
        const rows = this.sourceRows.filter((r) => r.source !== '0')
        this.sourceChart = {
            series: [{ name: 'Leads', data: rows.map((r) => r.leads) }],
            categories: rows.map((r) => r.source),
            colors: rows.map((r) => (r.isGoogleAds ? '#4285f4' : stableColorForString(r.source, CHART_PALETTE))),
        }
    }

    // --- Campaign Breakdown ---

    private buildCampaignRows(): void {
        const vars = this.globalVariablesService.currentVariables
        const gatewayRate = (vars?.paygateGatewayFeePercentage ?? 3.5) / 100

        const campaignMap = new Map<string, CampaignRow>()

        for (const client of this.googleAdsClients) {
            const campaign = client.p_campaign || 'No Campaign'
            const row = campaignMap.get(campaign) || {
                campaign,
                leads: 0,
                conversions: 0,
                subscriptions: 0,
                conversionRate: 0,
                totalBays: 0,
                actualValue: 0,
                netRevenue: 0,
            }

            row.leads++
            row.totalBays += client.numberOfBays || 0

            if (client.leadStatus === LeadStatus.CONVERTED) {
                row.conversions++

                const summaries = (client as any).subscriptionSummaries
                if (summaries) {
                    for (const [subKey, sub] of Object.entries(summaries) as [string, any][]) {
                        if (sub.status === 'Active' || sub.status === 'Completed') {
                            row.subscriptions++
                            const subValue = ((sub.feeInCents || 0) / 100) * (sub.baysBooked || 1) * (sub.period || 1)
                            const commPct = this.subCommissionMap.get(subKey) ?? 15
                            row.actualValue += subValue
                            row.netRevenue += subValue * (commPct / 100) - subValue * gatewayRate
                        }
                    }
                }
            }

            row.conversionRate = row.leads > 0 ? (row.conversions / row.leads) * 100 : 0
            campaignMap.set(campaign, row)
        }

        this.campaignRows = Array.from(campaignMap.values()).sort((a, b) => b.leads - a.leads)
    }

    sortCampaignTable(column: keyof CampaignRow): void {
        if (this.campaignSortColumn === column) {
            this.campaignSortDirection = this.campaignSortDirection === 'asc' ? 'desc' : 'asc'
        } else {
            this.campaignSortColumn = column
            this.campaignSortDirection = 'desc'
        }

        this.campaignRows = [...this.campaignRows].sort((a, b) => {
            const aVal = a[column] as number
            const bVal = b[column] as number
            return this.campaignSortDirection === 'asc' ? aVal - bVal : bVal - aVal
        })
    }

    // --- Funnel Chart ---

    private buildFunnelChart(): void {
        const statusOrder = [
            LeadStatus.NEW,
            LeadStatus.CONTACT_ATTEMPTED,
            LeadStatus.CONTACTED,
            LeadStatus.PROSPECTING,
            LeadStatus.NEGOTIATING,
            LeadStatus.CONVERTED,
            LeadStatus.CLOSED,
        ]

        const statusCounts = new Map<string, number>()
        for (const status of statusOrder) {
            statusCounts.set(status, 0)
        }

        for (const client of this.googleAdsClients) {
            const status = client.leadStatus || LeadStatus.NEW
            statusCounts.set(status, (statusCounts.get(status) || 0) + 1)
        }

        const statusColors: { [key: string]: string } = {
            [LeadStatus.NEW]: '#6b7280',
            [LeadStatus.CONTACT_ATTEMPTED]: '#94a3b8',
            [LeadStatus.CONTACTED]: '#3e8bff',
            [LeadStatus.PROSPECTING]: '#818cf8',
            [LeadStatus.NEGOTIATING]: '#a78bfa',
            [LeadStatus.CONVERTED]: '#4bd181',
            [LeadStatus.CLOSED]: '#ef4444',
        }

        const categories = statusOrder.filter((s) => (statusCounts.get(s) || 0) > 0)
        const data = categories.map((s) => statusCounts.get(s) || 0)
        const colors = categories.map((s) => statusColors[s] || '#6b7280')

        this.funnelChart = {
            series: [{ name: 'Google Ads Leads', data }],
            categories,
            colors,
        }
    }

    // --- Assignee Chart ---

    private buildAssigneeChart(): void {
        const assigneeCounts = new Map<string, number>()

        for (const client of this.googleAdsClients) {
            const uid = client.userResponsible || '_unassigned'
            assigneeCounts.set(uid, (assigneeCounts.get(uid) || 0) + 1)
        }

        const sorted = Array.from(assigneeCounts.entries()).sort((a, b) => b[1] - a[1])

        const categories = sorted.map(([uid]) => {
            if (uid === '_unassigned') return 'Not Assigned'
            const assignee = this.crmAssigneeMap[uid]
            return assignee?.displayName || uid
        })
        const data = sorted.map(([, count]) => count)
        const colors = sorted.map(([uid]) => {
            if (uid === '_unassigned') return '#6b7280'
            const assignee = this.crmAssigneeMap[uid]
            return assignee?.badgeColor || stableColorForString(uid, CHART_PALETTE)
        })

        this.assigneeChart = { series: [{ name: 'Google Ads Leads', data }], categories, colors }
    }

    // --- Monthly Trend ---

    private buildTrendChart(): void {
        const buckets = new Map<string, MonthBucket>()

        for (const client of this.allClients) {
            if (!client.createdAt) continue
            const d = (client.createdAt as any).toDate ? (client.createdAt as any).toDate() : new Date(client.createdAt as any)
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
            const label = d.toLocaleString('default', { month: 'short', year: 'numeric' })

            const bucket = buckets.get(key) || { label, leads: 0, googleAdsLeads: 0, conversions: 0 }
            bucket.leads++
            if (this.isGoogleAdsSource(client.p_source)) {
                bucket.googleAdsLeads++
                if (client.leadStatus === LeadStatus.CONVERTED) {
                    bucket.conversions++
                }
            }
            buckets.set(key, bucket)
        }

        const sorted = Array.from(buckets.entries()).sort((a, b) => a[0].localeCompare(b[0]))
        const categories = sorted.map(([, b]) => b.label)

        this.trendChart = {
            series: [
                { name: 'All Leads', data: sorted.map(([, b]) => b.leads) },
                { name: 'Google Ads Leads', data: sorted.map(([, b]) => b.googleAdsLeads) },
                { name: 'Google Ads Conversions', data: sorted.map(([, b]) => b.conversions) },
            ],
            categories,
            colors: ['#94a3b8', '#4285f4', '#4bd181'],
        }
    }

    // --- Date Range Helpers ---

    getDateRangeStart(dateRange: string): Date | null {
        const now = new Date()
        switch (dateRange) {
            case 'thismonth':
                return new Date(now.getFullYear(), now.getMonth(), 1)
            case 'lastmonth':
                return new Date(now.getFullYear(), now.getMonth() - 1, 1)
            case 'last7days':
                return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
            case 'last30days':
                return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
            case 'last3months':
                return new Date(now.getFullYear(), now.getMonth() - 3, now.getDate())
            case 'last6months':
                return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate())
            case 'last12months':
                return new Date(now.getFullYear(), now.getMonth() - 12, now.getDate())
            case 'thisyear':
                return new Date(now.getFullYear(), 0, 1)
            case 'custom':
                const customStart = this.filterForm.get('customStartDate')?.value
                return customStart ? new Date(customStart) : null
            case 'alltime':
            default:
                return null
        }
    }

    getDateRangeEnd(dateRange: string): Date | null {
        if (dateRange === 'lastmonth') {
            const now = new Date()
            const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0)
            endOfLastMonth.setHours(23, 59, 59, 999)
            return endOfLastMonth
        }
        if (dateRange === 'custom') {
            const customEnd = this.filterForm.get('customEndDate')?.value
            if (customEnd) {
                const endDate = new Date(customEnd)
                endDate.setHours(23, 59, 59, 999)
                return endDate
            }
        }
        return null
    }

    onAdSpendChange(value: string): void {
        const parsed = parseFloat(value)
        this.adSpend = isNaN(parsed) || parsed <= 0 ? null : parsed
    }
}
